// Shows or hides masked inputs behind an eye button. One button may control
// several inputs. An input is masked either by type="password" or, where a
// password field would draw password-manager prompts, by CSS keyed on
// data-masked.
export function bindRevealToggle(
    button: HTMLElement | null,
    inputs: ArrayLike<HTMLInputElement>
): void {
    // initRevealToggles re-runs on every htmx settle; a second listener would
    // undo the first on each click.
    if (!button || !inputs.length || button.dataset.revealBound) return;
    button.dataset.revealBound = 'true';

    button.addEventListener('click', () => {
        const reveal = isMasked(inputs[0]);
        for (const input of Array.from(inputs)) {
            setMasked(input, !reveal);
        }
        const icon = button.querySelector('i');
        if (icon) {
            icon.classList.toggle('fa-eye', !reveal);
            icon.classList.toggle('fa-eye-slash', reveal);
        }
        button.setAttribute('aria-pressed', String(reveal));
    });
}

function isMasked(input: HTMLInputElement): boolean {
    if (input.dataset.masked !== undefined) {
        return input.dataset.masked === 'true';
    }
    return input.type === 'password';
}

function setMasked(input: HTMLInputElement, masked: boolean): void {
    if (input.dataset.masked !== undefined) {
        input.dataset.masked = String(masked);
    } else {
        input.type = masked ? 'password' : 'text';
    }
}

// Where the CSS mask is unsupported, or support can't be confirmed, a password
// input is the only way to keep the value off screen, so take it despite the
// password-manager prompts.
export function fallBackToPasswordMask(root: ParentNode = document): void {
    const cssMasks =
        typeof CSS !== 'undefined' &&
        typeof CSS.supports === 'function' &&
        CSS.supports('-webkit-text-security', 'disc');
    if (cssMasks) return;
    root.querySelectorAll<HTMLInputElement>('input[data-masked]').forEach(
        input => {
            const masked = input.dataset.masked === 'true';
            delete input.dataset.masked;
            input.type = masked ? 'password' : 'text';
        }
    );
}

// Binds each [data-reveal-toggle] button to the input whose id it names.
export function initRevealToggles(root: ParentNode = document): void {
    fallBackToPasswordMask(root);
    root.querySelectorAll<HTMLElement>('[data-reveal-toggle]').forEach(
        button => {
            const input = document.getElementById(
                button.dataset.revealToggle ?? ''
            );
            if (input instanceof HTMLInputElement) {
                bindRevealToggle(button, [input]);
            } else {
                console.warn(
                    `reveal toggle target not found: ${button.dataset.revealToggle}`
                );
            }
        }
    );
}

document.addEventListener('DOMContentLoaded', () => initRevealToggles());
document.addEventListener('htmx:afterSettle', () => initRevealToggles());
